import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  deleteReport,
  getReports,
  ReportRow,
  ReportStatus,
  setReportStatus,
} from '@/services/reportsService';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Trash2, RefreshCw, Flag } from 'lucide-react';

const Reports = () => {
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<ReportStatus | 'all'>('all');
  const [searchItemId, setSearchItemId] = useState('');
  const [mutatingId, setMutatingId] = useState<string | null>(null);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const data = await getReports(statusFilter === 'all' ? undefined : statusFilter);
      setReports(data);
    } catch (e: any) {
      console.error('Error fetching reports:', e);
      toast.error(e?.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const filtered = useMemo(() => {
    const itemId = parseInt(searchItemId);
    if (!searchItemId.trim()) return reports;
    if (isNaN(itemId)) return reports;
    return reports.filter((r) => r.item_id === itemId);
  }, [reports, searchItemId]);

  const statusBadge = (s: ReportStatus) => {
    if (s === 'resolved') return <Badge className="bg-emerald-600 hover:bg-emerald-600">Resolved</Badge>;
    return <Badge variant="secondary">Pending</Badge>;
  };

  const typeBadge = (t: string) => {
    if (t === 'broken_link') return <Badge variant="outline">Broken link</Badge>;
    if (t === 'incorrect_info') return <Badge variant="outline">Incorrect info</Badge>;
    return <Badge variant="outline">Other</Badge>;
  };

  const handleSetStatus = async (id: string, status: ReportStatus) => {
    try {
      setMutatingId(id);
      const updated = await setReportStatus(id, status);
      setReports((prev) => prev.map((r) => (r.id === id ? updated : r)));
      toast.success(`Report marked ${status}`);
    } catch (e: any) {
      console.error('Error updating report:', e);
      toast.error(e?.message || 'Failed to update report');
    } finally {
      setMutatingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setMutatingId(id);
      await deleteReport(id);
      setReports((prev) => prev.filter((r) => r.id !== id));
      toast.success('Report deleted');
    } catch (e: any) {
      console.error('Error deleting report:', e);
      toast.error(e?.message || 'Failed to delete report');
    } finally {
      setMutatingId(null);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reports</h1>
          <p className="text-muted-foreground">Review and manage user reports</p>
        </div>
        <Button variant="outline" onClick={fetchReports} disabled={loading}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      <Card className="bg-background border border-border/50 shadow-subtle">
        <CardHeader className="pb-2">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <CardTitle>Reports</CardTitle>
              <CardDescription>
                {loading ? 'Loading...' : `Showing ${filtered.length} of ${reports.length} reports`}
              </CardDescription>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                placeholder="Filter by item id (e.g. 123)"
                value={searchItemId}
                onChange={(e) => setSearchItemId(e.target.value)}
                className="w-full sm:w-[220px]"
              />
              <Select
                value={statusFilter}
                onValueChange={(v) => setStatusFilter(v as any)}
              >
                <SelectTrigger className="w-full sm:w-[170px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
                <span className="text-sm text-muted-foreground">Loading reports...</span>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <div className="w-16 h-16 rounded-full bg-muted/30 flex items-center justify-center">
                <Flag className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium">No reports found</h3>
                <p className="text-muted-foreground">Try changing the filters</p>
              </div>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden md:table-cell">User</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-xs">
                        <Link to={`/admin/clothinglist/${r.item_id}`} className="text-primary hover:underline">
                          #{r.item_id}
                        </Link>
                      </TableCell>
                      <TableCell>{typeBadge(r.report_type)}</TableCell>
                      <TableCell>{statusBadge(r.status)}</TableCell>
                      <TableCell className="hidden md:table-cell">
                        <span className="font-mono text-xs text-muted-foreground">{r.user_id}</span>
                      </TableCell>
                      <TableCell className="max-w-[380px]">
                        <span className="text-sm text-muted-foreground line-clamp-2">
                          {r.description || '(no description)'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Select
                            value={r.status}
                            onValueChange={(v) => handleSetStatus(r.id, v as ReportStatus)}
                            disabled={mutatingId === r.id}
                          >
                            <SelectTrigger className="h-8 w-[120px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="resolved">Resolved</SelectItem>
                            </SelectContent>
                          </Select>

                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" disabled={mutatingId === r.id}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-background border border-border/50">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete report?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This deletes the report record. It does not delete the product.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-background hover:bg-muted">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-destructive text-white hover:bg-destructive/90"
                                  onClick={() => handleDelete(r.id)}
                                  disabled={mutatingId === r.id}
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Reports;

